// FeedMessages.jsx
import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import ProfileCardItem from '../container/ProfileCardItem.js';
import { ProfileCardItemPlaceholder } from '../component/ProfileCardItem.jsx';
import OutboxMessage from '../container/OutboxMessage.js';
import MessageCard from '../container/MessageCard.js';
import FlixListItem from '../container/FlixListItem.js';

import { SanitizedLink as Link } from '../component/SanitizedLink.jsx';
import HydrationBoundary from '../component/HydrationBoundary.jsx';
import Loading from '../component/Loading.jsx';
import {
  InfiniteScroller,
  ScrollItem,
} from '../component/InfiniteScroller.jsx';

import {
  CategoryLayout,
  CategoryComponentUi,
  FeedCardSize,
} from '../resource/feedConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { getIsInBrowserMainThread } from '../resource/getJsEnvironment.js';
import { ButtonId } from '../resource/trackingConstants.js';

import media from '../style/media.js';
import TextEllipsis from '../style/TextEllipsis.js';

import MoreIconSource from '../../img/ic_arrow_right_white.svg';
import { color } from '../style/variables.js';

const isClient = getIsInBrowserMainThread();

const {
  MESSAGE_CARD_WIDTH,
  MESSAGE_CARD_WIDTH_DESKTOP,
  MESSAGE_CARD_HEIGHT,
  MESSAGE_CARD_HEIGHT_DESKTOP,
  MESSAGE_METADATA_CARD_WIDTH,
  MESSAGE_METADATA_CARD_DESKTOP_WIDTH,
  SHORTS_CARD_WIDTH,
  SHORTS_CARD_WIDTH_DESKTOP,
  SHORTS_CARD_HEIGHT,
  SHORTS_CARD_HEIGHT_DESKTOP,
  SHORTS_METADATA_CARD_WIDTH,
  SHORTS_METADATA_CARD_WIDTH_DESKTOP,
  PROFILE_CARD_HEIGHT,
  PROFILE_CARD_HEIGHT_DESKTOP,
} = FeedCardSize;

export const FeedMessages = ({
  title = '',
  category = '',
  titleLink = '',
  moreLink = '',
  layout = CategoryLayout.ROW,
  componentUi = CategoryComponentUi.MESSAGE_CARD,
  categoryIndex = null,
  nextPage = null,
  feedUnixTimestamp = null,
  maxPlaceholder = 10,
  totalCount = 0,
  itemCountPerPage = 20,
  shouldRefresh = false,
  shouldShowTitle = false,
  shouldShowTotalCount = false,
  shouldShowMoreLink = false,
  isAuthed = false,
  isFirstPageFetched = false,
  isNextPageFetching = false,
  isShorts = false,
  isImage = false,
  isTitleClickable = false,
  isProfileCategory = false,
  messageIds = [],
  pinnedItemIds = [],
  listPath = [],
  locationState = {},
  fetchFeeds = () => null,
  ...restProps
}) => {
  const shouldShowHeader = shouldShowTitle || shouldShowMoreLink;
  const isProfileCardItemUi =
    CategoryComponentUi.PROFILE_CARD_ITEM === componentUi;
  const isMessageCardUi = CategoryComponentUi.MESSAGE_CARD === componentUi;
  const isFlixListItemUi = CategoryComponentUi.FLIX_LIST_ITEM === componentUi;
  const isRowLayout = CategoryLayout.ROW === layout;
  const { t } = useTranslation();
  const fetchFeedsByPage = useCallback(
    page => {
      fetchFeeds({
        type: category,
        page,
        limit: itemCountPerPage,
        unixTimestamp: feedUnixTimestamp,
        shouldSkipLocalCache: shouldRefresh,
      });
    },
    [category, feedUnixTimestamp, fetchFeeds, itemCountPerPage, shouldRefresh]
  );
  useEffect(() => {
    fetchFeedsByPage(1);
  }, [
    category,
    feedUnixTimestamp,
    fetchFeeds,
    fetchFeedsByPage,
    isAuthed,
    shouldRefresh,
  ]);
  useEffect(() => {
    return () => {
      if (!window.__IS_HYDRATED__) {
        window.__IS_HYDRATED__ = 'FeedMessages';
      }
    };
  }, []);
  const messageListLocationState = useMemo(
    () => ({
      ...locationState,
      listPath,
      category,
    }),
    [category, listPath, locationState]
  );
  const renderMessage = (messageId, index) => {
    const shouldHydrate = !isClient || !window.__IS_HYDRATED__;
    const reactKey = messageId || index;
    switch (layout) {
      case CategoryLayout.LIST: {
        if (isFlixListItemUi) {
          return (
            <ObservableFlixListItemMessage
              key={reactKey}
              shouldHydrate={shouldHydrate}
            >
              <FlixListItem
                shouldUseLink
                shouldUseLazyImage={false}
                messageId={messageId}
                isPinned={pinnedItemIds.includes(messageId)}
                id={messageId}
                categoryId={category}
                itemIndexInCategory={index}
                {...restProps}
              />
            </ObservableFlixListItemMessage>
          );
        }
        return (
          <ListOutboxMessageWrapper
            key={reactKey}
            as={Link}
            to={`/${isImage ? 'post' : 'story'}/${messageId}${isClient ? location.search : ''}`}
            data-element_id={ButtonId.Profile.ButtonPostClick}
            data-tracking_payload={{
              messageId,
              'discover.index': categoryIndex,
              'discover.category': category,
            }}
            state={messageListLocationState}
          >
            <OutboxMessage
              messageId={messageId}
              isPinned={pinnedItemIds.includes(messageId)}
              id={messageId}
              categoryId={category}
              itemIndexInCategory={index}
              {...restProps}
            />
          </ListOutboxMessageWrapper>
        );
      }
      case CategoryLayout.GRID:
      case CategoryLayout.ROW:
      default: {
        const Component = isProfileCardItemUi ? (
          <ProfileCardItem
            id={messageId}
            itemIndexInCategory={index}
            messageId={messageId}
            categoryId={category}
            categoryIndex={categoryIndex}
            index={index}
            isPinned={pinnedItemIds.includes(messageId)}
            isShorts={isShorts}
            locationState={messageListLocationState}
            {...restProps}
          />
        ) : (
          <MessageCard
            id={messageId}
            categoryId={category}
            categoryIndex={categoryIndex}
            itemIndexInCategory={index}
            listPath={listPath}
            isShorts={isShorts}
            shouldWrapLink
            shouldTrackEvent
            shouldShowUser
            shouldShowTooltip
            shouldUseLazyThumbnail={false}
            {...restProps}
          />
        );

        if (CategoryLayout.GRID === layout) {
          return (
            <ObservableGridMessage
              key={reactKey}
              shouldHydrate={shouldHydrate}
              isMessageCardUi={isMessageCardUi}
              isProfileCardItemUi={isProfileCardItemUi}
              isShorts={isShorts}
            >
              {Component}
            </ObservableGridMessage>
          );
        }
        return (
          <ObservableRowMessage
            key={reactKey}
            shouldHydrate={shouldHydrate}
            isProfileCardItemUi={isProfileCardItemUi}
            isShorts={isShorts}
          >
            {Component}
          </ObservableRowMessage>
        );
      }
    }
  };
  /**
   * Render `ProfileCardItem` as grid layout.
   */
  const renderGridProfileCardItems = () => {
    return (
      <GridProfileCardItems isShorts={isShorts}>
        <ScrollItem
          loader={
            <GridMessageWrapper isMessageCardUi={isMessageCardUi}>
              <Loading />
            </GridMessageWrapper>
          }
        >
          {renderMessages()}
        </ScrollItem>
      </GridProfileCardItems>
    );
  };
  /**
   * Render `MessageCard` as grid layout.
   */
  const renderGridMessageCards = () => {
    return (
      <GridMessageCards>
        <ScrollItem
          loader={
            <GridMessageWrapper isMessageCardUi={isMessageCardUi}>
              <Loading />
            </GridMessageWrapper>
          }
        >
          {renderMessages()}
        </ScrollItem>
      </GridMessageCards>
    );
  };
  /**
   * Render `FlixListItem` as list layout.
   */
  const renderListFlixListItems = () => {
    return (
      <ListFlixListItems>
        <ScrollItem
          loader={
            <LoadingWrapper>
              <Loading />
            </LoadingWrapper>
          }
        >
          {renderMessages()}
        </ScrollItem>
      </ListFlixListItems>
    );
  };
  /**
   * Render `OutboxMessage` as list layout.
   */
  const renderListOutboxMessageItems = () => {
    return (
      <ListOutboxMessageItems>
        <ScrollItem
          loader={
            <LoadingWrapper>
              <Loading />
            </LoadingWrapper>
          }
        >
          {renderMessages()}
        </ScrollItem>
      </ListOutboxMessageItems>
    );
  };
  /**
   * Render `ProfileCardItem` as row layout.
   */
  const renderRowProfileCardItems = () => {
    return (
      <RowProfileCardItemsWrapper>
        <RowProfileCardItems>
          <ScrollItem
            loader={
              <RowMessageWrapper
                isShorts={isShorts}
                isProfileCardItemUi={isProfileCardItemUi}
              >
                <Loading />
              </RowMessageWrapper>
            }
          >
            {renderMessages()}
          </ScrollItem>
        </RowProfileCardItems>
      </RowProfileCardItemsWrapper>
    );
  };
  /**
   * Render `MessageCard` as row layout.
   */
  const renderRowMessageCards = () => {
    return (
      <RowMessageCardsWrapper>
        <RowMessageCards>
          <ScrollItem
            loader={
              <RowMessageWrapper
                isShorts={isShorts}
                isProfileCardItemUi={isProfileCardItemUi}
              >
                <Loading />
              </RowMessageWrapper>
            }
          >
            {renderMessages()}
          </ScrollItem>
        </RowMessageCards>
      </RowMessageCardsWrapper>
    );
  };
  const renderItemsByLayout = () => {
    return CategoryLayout.GRID === layout
      ? isProfileCardItemUi
        ? renderGridProfileCardItems()
        : renderGridMessageCards()
      : CategoryLayout.LIST === layout
        ? isFlixListItemUi
          ? renderListFlixListItems()
          : renderListOutboxMessageItems()
        : isProfileCardItemUi
          ? renderRowProfileCardItems()
          : renderRowMessageCards();
  };
  const renderMessages = () =>
    messageIds.length
      ? messageIds.map(renderMessage)
      : Array(maxPlaceholder).fill(null).map(renderMessage);
  if (isFirstPageFetched && !messageIds.length) return null;
  return (
    <StyledFeedStoryGrid>
      {shouldShowHeader && (
        <Header>
          {shouldShowTitle && (
            <Title
              to={titleLink}
              as={isTitleClickable ? Link : undefined}
              replace={isProfileCategory}
            >
              <TitleText>{title}</TitleText>
              {shouldShowTotalCount && (
                <TotalCount>({totalCount || 0})</TotalCount>
              )}
            </Title>
          )}
          {shouldShowMoreLink && (
            <MoreLink
              to={moreLink}
              data-element_id={ButtonId.All.ButtonMore}
              data-tracking_payload={{
                'discover.index': categoryIndex,
                'discover.category': category,
              }}
            >
              {t('more', {
                ns: TranslationNamespace.GENERAL,
              })}
              <img src={MoreIconSource} aria-hidden />
            </MoreLink>
          )}
        </Header>
      )}
      <Body $isRowLayout={isRowLayout}>
        <InfiniteScroller
          {...(CategoryLayout.ROW === layout
            ? {
                axis: 'x',
                useWindow: false,
              }
            : {
                useWindow: true,
              })}
          pageStart={1}
          threshold={100}
          hasMore={nextPage != null}
          isFetching={isNextPageFetching || !isFirstPageFetched}
          loadMore={() => {
            fetchFeeds({
              type: category,
              page: nextPage,
              limit: itemCountPerPage,
              unixTimestamp: feedUnixTimestamp,
            });
          }}
        >
          {renderItemsByLayout()}
        </InfiniteScroller>
      </Body>
    </StyledFeedStoryGrid>
  );
};

FeedMessages.propTypes = {
  title: PropTypes.string,
  category: PropTypes.string,
  titleLink: PropTypes.string,
  moreLink: PropTypes.string,
  layout: PropTypes.string,
  componentUi: PropTypes.string,
  categoryIndex: PropTypes.number,
  nextPage: PropTypes.number,
  feedUnixTimestamp: PropTypes.number,
  maxPlaceholder: PropTypes.number,
  totalCount: PropTypes.number,
  itemCountPerPage: PropTypes.number,
  isAuthed: PropTypes.bool,
  isFirstPageFetched: PropTypes.bool,
  isNextPageFetching: PropTypes.bool,
  isProfileCategory: PropTypes.bool,
  isShorts: PropTypes.bool,
  isImage: PropTypes.bool,
  isTitleClickable: PropTypes.bool,
  shouldRefresh: PropTypes.bool,
  shouldShowTitle: PropTypes.bool,
  shouldShowTotalCount: PropTypes.bool,
  shouldShowMoreLink: PropTypes.bool,
  messageIds: PropTypes.array,
  pinnedItemIds: PropTypes.array,
  listPath: PropTypes.array,
  locationState: PropTypes.object,
  fetchFeeds: PropTypes.func,
};

const HorizontalPadding = css`
  padding-left: 16px;
  padding-right: 16px;
  ${media.mobile`
  `}
`;

const HideScrollbar = css`
  &::-webkit-scrollbar {
    display: none;
  }
`;

const StyledFeedStoryGrid = styled.div``;

const Header = styled.header`
  ${HorizontalPadding};
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h2`
  overflow: hidden;
  margin-right: 10px;
  // add min-height: font-size * line-height to avoid layout shift
  min-height: 30px;
  flex: auto;
  display: flex;
  align-items: center;
  font-size: 20px;
  font-weight: 600;
  line-height: 150%;
  ${media.mobile`
    // add min-height: font-size * line-height to avoid layout shift
    min-height: 27px;
    font-size: 18px;
  `}
`;

const TitleText = styled.div`
  ${TextEllipsis};
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 150%;
  ${media.mobile`
        font-size: 18px;
  `}
`;

const TotalCount = styled.span`
  padding-left: 8px;
  color: var(--font-color-primary-on-dark, ${color.white});
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: 0.014px;
  ${media.mobile`
    font-size: 12px;
    letter-spacing: 0.012px;
  `}
`;

const MoreLink = styled(Link)`
  flex: none;
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 14px;
  line-height: 150%;
  ${media.mobile`
    font-size: 12px;
  `}
  >img {
    margin-left: 4px;
    width: 16px;
    height: 16px;
    object-fit: contain;
  }
`;

const Body = styled.div`
  ${({ $isRowLayout }) => css`
    min-height: ${$isRowLayout ? 'unset' : '75vh'};
    padding-bottom: ${$isRowLayout ? 'unset' : '40px'};
  `};
`;

const GridProfileCardItems = styled.div`
  ${HorizontalPadding};
  ${({ isShorts }) => css`
    padding-top: 16px;
    padding-bottom: 16px;
    display: grid;
    gap: 20px;
    justify-content: center;
    grid-template-columns: repeat(
      auto-fill,
      minmax(${isShorts ? 168 : 200}px, 1fr)
    );
    place-items: center;
    ${media.tablet`
      gap: 8px;
      grid-template-columns: repeat(
        auto-fill,
        minmax(${isShorts ? 112 : 140}px, 1fr)
      );
    `};
  `}
`;

const GridMessageCards = styled.div`
  ${HorizontalPadding};
  padding-top: 12px;
  padding-bottom: 12px;
  display: grid;
  grid-column-gap: 8px;
  grid-row-gap: 12px;
  grid-template-columns: repeat(auto-fill, minmax(148px, 1fr));
  ${media.mobile`
    grid-template-columns: repeat(auto-fill, minmax(128px, 1fr));
  `};
`;

const ListFlixListItems = styled.div`
  ${HideScrollbar};
`;

const ListOutboxMessageItems = styled.div`
  ${HideScrollbar};
`;

const RowProfileCardItemsWrapper = styled.div`
  ${HorizontalPadding};
  ${HideScrollbar};
  overflow-x: auto;
  overflow-y: hidden;
  font-size: 0px;
`;

const RowProfileCardItems = styled.div`
  display: inline-flex;
`;

const RowMessageCardsWrapper = styled.div`
  ${HorizontalPadding};
  ${HideScrollbar};
  overflow-x: auto;
  overflow-y: hidden;
`;

const RowMessageCards = styled.div`
  display: inline-flex;
`;

const GridMessageWrapper = styled.div`
  width: 100%;
  height: 100%;
  ${({ isMessageCardUi }) => {
    if (isMessageCardUi) {
      return css`
        aspect-ratio: ${MESSAGE_CARD_WIDTH / MESSAGE_CARD_HEIGHT};
        @supports not (aspect-ratio: auto) {
          position: relative;
          padding-top: ${`${parseInt(
            (MESSAGE_CARD_HEIGHT / MESSAGE_CARD_WIDTH) * 100
          )}%`};
          > div {
            position: absolute;
            top: 0;
            left: 0;
            bottom: 0;
            right: 0;
          }
        }
      `;
    }
  }}
`;

const ListFlixListItemWrapper = styled.div`
  ${HorizontalPadding};
  margin-bottom: 8px;
  border-bottom: 1px solid ${color.neutral['30_alpha_15']};
  padding-bottom: 8px;
  :first-child {
    margin-top: 12px;
  }
  :last-child {
    margin-bottom: 0px;
    border-bottom: none;
  }
`;

const ListOutboxMessageWrapper = styled.div`
  ${HorizontalPadding};
  border-bottom: 1px solid ${color.neutral['30_alpha_15']};
  display: block;
  :last-child {
    border-bottom: none;
  }
  ${media.mobile`
  `};
`;

const RowMessageWrapper = styled.div`
  width: ${({ isShorts, isProfileCardItemUi }) =>
    isShorts
      ? isProfileCardItemUi
        ? SHORTS_METADATA_CARD_WIDTH_DESKTOP
        : SHORTS_CARD_WIDTH_DESKTOP
      : isProfileCardItemUi
        ? MESSAGE_METADATA_CARD_DESKTOP_WIDTH
        : MESSAGE_CARD_WIDTH_DESKTOP}px;
  height: ${({ isShorts, isProfileCardItemUi }) =>
    isProfileCardItemUi
      ? 'auto'
      : isShorts
        ? `${SHORTS_CARD_HEIGHT_DESKTOP}px`
        : `${MESSAGE_CARD_HEIGHT_DESKTOP}px`};
  min-height: ${({ isProfileCardItemUi }) =>
    isProfileCardItemUi ? `${PROFILE_CARD_HEIGHT_DESKTOP}px` : 'initial'};
  margin-right: 12px;

  :last-child {
    margin-right: 0;
  }
  ${media.tablet`
    margin-right: 8px;
    width: ${({ isShorts, isProfileCardItemUi }) =>
      isShorts
        ? isProfileCardItemUi
          ? SHORTS_METADATA_CARD_WIDTH
          : SHORTS_CARD_WIDTH
        : isProfileCardItemUi
          ? MESSAGE_METADATA_CARD_WIDTH
          : MESSAGE_CARD_WIDTH}px;
    height: ${({ isShorts, isProfileCardItemUi }) =>
      isProfileCardItemUi
        ? 'auto'
        : isShorts
          ? `${SHORTS_CARD_HEIGHT}px`
          : `${MESSAGE_CARD_HEIGHT}px`};
    min-height: ${({ isProfileCardItemUi }) => (isProfileCardItemUi ? `${PROFILE_CARD_HEIGHT}px` : 'initial')};
  `};
`;

const LoadingWrapper = styled.div`
  padding-top: 20px;
  padding-bottom: 20px;
`;

const OBSERVER_OPTIONS = { threshold: 0.2, triggerOnce: true };

const ObservableFlixListItemMessage = ({ shouldHydrate, children }) => {
  const { ref, inView } = useInView(OBSERVER_OPTIONS);
  if (shouldHydrate) {
    return (
      <HydrationBoundary
        shouldHydrate={inView}
        wrapper={<ListFlixListItemWrapper ref={ref} />}
      >
        {children}
      </HydrationBoundary>
    );
  }
  if (inView) {
    return (
      <ListFlixListItemWrapper ref={ref}>{children}</ListFlixListItemWrapper>
    );
  }
  return <ListFlixListItemWrapper ref={ref} />;
};

ObservableFlixListItemMessage.propTypes = {
  shouldHydrate: PropTypes.bool,
  children: PropTypes.node,
};

const ObservableGridMessage = ({
  shouldHydrate,
  isMessageCardUi,
  isProfileCardItemUi,
  isShorts,
  children,
}) => {
  const { ref, inView } = useInView(OBSERVER_OPTIONS);
  if (shouldHydrate) {
    return (
      <HydrationBoundary
        shouldHydrate={inView}
        wrapper={
          <GridMessageWrapper ref={ref} isMessageCardUi={isMessageCardUi} />
        }
      >
        {children}
      </HydrationBoundary>
    );
  }
  if (inView) {
    return (
      <GridMessageWrapper ref={ref} isMessageCardUi={isMessageCardUi}>
        {children}
      </GridMessageWrapper>
    );
  }
  if (isProfileCardItemUi) {
    return (
      <GridMessageWrapper ref={ref} isMessageCardUi={isMessageCardUi}>
        <ProfileCardItemPlaceholder isShorts={isShorts} />
      </GridMessageWrapper>
    );
  }
  return <GridMessageWrapper ref={ref} isMessageCardUi={isMessageCardUi} />;
};

ObservableGridMessage.propTypes = {
  shouldHydrate: PropTypes.bool,
  isMessageCardUi: PropTypes.bool,
  isProfileCardItemUi: PropTypes.bool,
  isShorts: PropTypes.bool,
  children: PropTypes.node,
};

const ObservableRowMessage = ({
  shouldHydrate,
  isProfileCardItemUi,
  isShorts,
  children,
}) => {
  const { ref, inView } = useInView(OBSERVER_OPTIONS);
  if (shouldHydrate) {
    return (
      <HydrationBoundary
        shouldHydrate={inView}
        wrapper={
          <RowMessageWrapper
            ref={ref}
            isShorts={isShorts}
            isProfileCardItemUi={isProfileCardItemUi}
          />
        }
      >
        {children}
      </HydrationBoundary>
    );
  }
  if (inView) {
    return (
      <RowMessageWrapper
        ref={ref}
        isShorts={isShorts}
        isProfileCardItemUi={isProfileCardItemUi}
      >
        {children}
      </RowMessageWrapper>
    );
  }
  if (isProfileCardItemUi) {
    return (
      <RowMessageWrapper
        ref={ref}
        isShorts={isShorts}
        isProfileCardItemUi={isProfileCardItemUi}
      >
        <ProfileCardItemPlaceholder isShorts={isShorts} />
      </RowMessageWrapper>
    );
  }
  return (
    <RowMessageWrapper
      ref={ref}
      isShorts={isShorts}
      isProfileCardItemUi={isProfileCardItemUi}
    />
  );
};

ObservableRowMessage.propTypes = {
  shouldHydrate: PropTypes.bool,
  isProfileCardItemUi: PropTypes.bool,
  isShorts: PropTypes.bool,
  children: PropTypes.node,
};

export default FeedMessages;
